import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
}

export interface AuthActions {
  signUp: (email: string, password: string, userData?: { full_name?: string; username?: string }) => Promise<{ error: AuthError | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithOAuth: (provider: 'google' | 'github') => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: { full_name?: string; username?: string; avatar_url?: string }) => Promise<{ error: AuthError | null }>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  initializeAuth: () => Promise<void>;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      session: null,
      loading: true,
      error: null,

      // Actions
      signUp: async (email, password, userData) => {
        set({ loading: true, error: null });

        try {
          const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: userData,
              emailRedirectTo: `${window.location.origin}/auth/callback`,
            },
          });

          if (error) {
            set({ error: error.message, loading: false });
            return { error };
          }

          // If signup is successful, the user will be set via the auth state change listener
          set({ loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
          set({ error: errorMessage, loading: false });
          return { error: { message: errorMessage } as AuthError };
        }
      },

      signInWithPassword: async (email, password) => {
        set({ loading: true, error: null });
        
        try {
          const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) {
            set({ error: error.message, loading: false });
            return { error };
          }

          // User and session will be set via the auth state change listener
          set({ loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
          set({ error: errorMessage, loading: false });
          return { error: { message: errorMessage } as AuthError };
        }
      },

      signInWithOAuth: async (provider) => {
        set({ loading: true, error: null });
        
        try {
          const { error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
            },
          });

          if (error) {
            set({ error: error.message, loading: false });
            return { error };
          }

          // OAuth redirect will handle the rest
          set({ loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
          set({ error: errorMessage, loading: false });
          return { error: { message: errorMessage } as AuthError };
        }
      },

      signOut: async () => {
        set({ loading: true, error: null });
        
        try {
          const { error } = await supabase.auth.signOut();

          if (error) {
            set({ error: error.message, loading: false });
            return { error };
          }

          // User and session will be cleared via the auth state change listener
          set({ loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
          set({ error: errorMessage, loading: false });
          return { error: { message: errorMessage } as AuthError };
        }
      },

      resetPassword: async (email) => {
        set({ loading: true, error: null });
        
        try {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/auth/reset-password`,
          });

          if (error) {
            set({ error: error.message, loading: false });
            return { error };
          }

          set({ loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
          set({ error: errorMessage, loading: false });
          return { error: { message: errorMessage } as AuthError };
        }
      },

      updatePassword: async (password) => {
        set({ loading: true, error: null });
        
        try {
          const { error } = await supabase.auth.updateUser({
            password,
          });

          if (error) {
            set({ error: error.message, loading: false });
            return { error };
          }

          set({ loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
          set({ error: errorMessage, loading: false });
          return { error: { message: errorMessage } as AuthError };
        }
      },

      updateProfile: async (updates) => {
        set({ loading: true, error: null });
        
        try {
          const { error } = await supabase.auth.updateUser({
            data: updates,
          });

          if (error) {
            set({ error: error.message, loading: false });
            return { error };
          }

          set({ loading: false });
          return { error: null };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
          set({ error: errorMessage, loading: false });
          return { error: { message: errorMessage } as AuthError };
        }
      },

      clearError: () => set({ error: null }),
      setLoading: (loading) => set({ loading }),

      initializeAuth: async () => {
        set({ loading: true });

        try {
          // Get initial session
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error) {
            console.error('Error getting session:', error);
            set({ user: null, session: null, loading: false });
            return;
          }

          set({ user: session?.user ?? null, session, loading: false });

          // Listen for auth state changes when available (e.g., browser runtime)
          if (typeof supabase.auth.onAuthStateChange === 'function') {
            supabase.auth.onAuthStateChange((event, session) => {
              if (import.meta.env.DEV) {
                console.log('Auth state changed:', event, session?.user?.id);
              }

              // Handle session expiry - redirect to login
              if (event === 'SIGNED_OUT') {
                set({ user: null, session: null, loading: false });
                // Only redirect if we're on a protected route
                if (window.location.pathname.startsWith('/app')) {
                  console.warn('Session expired, redirecting to login');
                  window.location.href = '/login?reason=session_expired';
                }
                return;
              }

              // Handle token refresh failure
              if (event === 'TOKEN_REFRESHED' && !session) {
                console.error('Token refresh failed - no session returned');
                set({ user: null, session: null, loading: false });
                if (window.location.pathname.startsWith('/app')) {
                  window.location.href = '/login?reason=session_expired';
                }
                return;
              }

              set({
                user: session?.user ?? null,
                session,
                loading: false,
              });
            });
          } else {
            console.warn('Supabase auth state change listener not available in this environment.');
          }
        } catch (err) {
          console.error('Error initializing auth:', err);
          set({ user: null, session: null, loading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist user and session, not loading or error states
        user: state.user,
        session: state.session,
      }),
    }
  )
);
